import { useState } from "react";
import { toast } from "sonner";
import { Trash, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { Panel } from "@/components/core/AppLayout";
import { DangerDialog } from "@/components/core/DangerDialog";
import { ConfirmDialog } from "@/components/core/ConfirmDialog";

export const TrashPanel = ({ refreshKey, onChanged }) => {
  const { data, reload } = useFetch(`/admin/cestino?k=${refreshKey}`);
  const [purge, setPurge] = useState(null);
  const [restore, setRestore] = useState(null);
  const done = () => { reload(); onChanged(); };
  const doRestore = async () => {
    try {
      const r = await api.post(`/admin/cestino/${restore.id}/ripristina`);
      toast.success(`«${restore.ragione_sociale}» ripristinato con tutti i dati` + (r.data.fatture_rinominate ? ` (${r.data.fatture_rinominate} fatture rinumerate con suffisso -R)` : ""));
      done();
    } catch (e) { toast.error(errMsg(e)); }
    setRestore(null);
  };
  const doPurge = async (conferma) => {
    try { await api.post(`/admin/cestino/${purge.id}/elimina`, { conferma }); toast.success("Eliminato definitivamente"); setPurge(null); done(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel className="p-6 mb-6" data-testid="trash-panel">
      <h3 className="font-display text-lg font-extrabold text-[#0A1128] flex items-center gap-2 mb-1"><Trash className="h-5 w-5 text-[#E8173A]" />Cestino</h3>
      <p className="text-sm text-slate-700 mb-4">I clienti eliminati restano qui per 7 giorni con tutti i dati collegati, poi vengono eliminati definitivamente in automatico. Il ripristino riporta tutto com'era; il link del portale torna disattivato per sicurezza.</p>
      {data && !data.length && <div className="text-sm text-slate-600" data-testid="trash-empty">Il cestino è vuoto.</div>}
      <div className="space-y-2">
        {(data || []).map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-4 rounded-xl bg-[#A6DAFC] px-4 py-3" data-testid={`trash-row-${t.id}`}>
            <div className="min-w-0">
              <div className="font-bold text-[#0A1128]">{t.ragione_sociale}</div>
              <div className="text-xs text-slate-600">Eliminato il {fmtDateTime(t.deleted_at)} da {t.deleted_by_nome} · {t.items.map((i) => `${i.label}: ${i.count}`).join(", ") || "nessun dato collegato"}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${t.giorni_rimasti <= 1 ? "bg-[#E8173A] text-white" : "bg-white/70 text-[#0A1128]"}`} data-testid={`trash-days-${t.id}`}>
                {t.giorni_rimasti === 1 ? "1 giorno rimasto" : `${t.giorni_rimasti} giorni rimasti`}</span>
              <Button size="sm" onClick={() => setRestore(t)} className="rounded-full bg-[#0A1128] hover:bg-[#E8173A] font-bold" data-testid={`trash-restore-${t.id}`}><RotateCcw className="h-4 w-4 mr-1" />Ripristina</Button>
              <Button size="sm" variant="outline" onClick={() => setPurge(t)} className="rounded-full font-bold text-[#C8102E] border-red-200" data-testid={`trash-purge-${t.id}`}><Trash2 className="h-4 w-4 mr-1" />Elimina definitivamente</Button>
            </div>
          </div>
        ))}
      </div>
      <ConfirmDialog open={!!restore} onOpenChange={(o) => !o && setRestore(null)} title="Ripristinare il cliente?" confirmLabel="Ripristina"
        description={`«${restore?.ragione_sociale}» tornerà visibile con tutti i dati collegati (documenti, task, PED, appuntamenti, accessi, fatture, appunti privati dei soci). Il portale resterà disattivato finché non lo riattivi.`} onConfirm={doRestore} />
      <DangerDialog open={!!purge} onOpenChange={(o) => !o && setPurge(null)} title="Eliminare definitivamente?" items={purge?.items || []}
        description={`Cliente: ${purge?.ragione_sociale}`} onConfirm={doPurge} confirmLabel="Elimina definitivamente" />
    </Panel>
  );
};
